import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthPageSkeleton } from '../components/common/LoadingSkeletons.js';
import { capturePostHogEvent } from '../lib/posthog.js';
import { buildActivationEventProperties } from '../../shared/analytics.js';
import { useAuth } from '../hooks/useAuth.js';
import {
  buildAuthReturnRedirectTarget,
  getRedirectTarget,
} from '../components/Auth/authEmailVerification.js';

interface PublicOnlyRouteProps {
  children: React.ReactNode;
}

export default function PublicOnlyRoute({ children }: PublicOnlyRouteProps) {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectTarget = React.useMemo(() => getRedirectTarget(location.search), [location.search]);
  const authLandingTarget = React.useMemo(
    () => buildAuthReturnRedirectTarget(redirectTarget),
    [redirectTarget],
  );

  React.useEffect(() => {
    if (!user || searchParams.get('verified') !== '1') {
      return;
    }

    const storageKey = `anysync:email-verified:${user.id}:${redirectTarget}`;
    if (window.sessionStorage.getItem(storageKey) === '1') {
      return;
    }

    capturePostHogEvent(
      'email_verified',
      buildActivationEventProperties(
        {
          activationMilestone: 'email_verified',
        },
        {
          verification_method: 'email_link',
          redirect_target: redirectTarget,
        },
      ),
    );
    window.sessionStorage.setItem(storageKey, '1');
  }, [redirectTarget, searchParams, user]);

  if (isLoading) {
    return <AuthPageSkeleton />;
  }

  if (user) {
    return <Navigate to={authLandingTarget} replace />;
  }

  return <>{children}</>;
}
